import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Dot } from 'recharts';
import { ChevronLeft } from 'lucide-react';
import { getPlayerTechnicalTrend, getTechnicalKpiBreakdown } from '../../api';
import { C, FONT_UI, FONT_MONO } from './dayspineTokens';

// Player app's "Technical data" page — same computation as the staff-facing Technical 360
// team table (src/pages/TechnicalOverview.js) and the same KpiCell display convention (rate
// KPIs show "X% (count)", volume/bonus KPIs show the raw count, never the internal placeholder-
// scaled `value`) — see that file's KpiCell comment. Backend: GET .../technical-trend calls the
// exact same computeTechnicalBreakdown() the admin table uses, once per one of this player's
// last 8 matches for the chart, and once pooled across those same matches for the table below.
// Laid out in the "Day spine v2" light card style (dayspineTokens.js) to match the Today screen.
//
// Game selector (multi-select, below the chart): re-pools the table (and the OVERALL badge)
// down to just the picked games, by calling the SAME endpoint the staff match-picker on
// TechnicalOverview.js already uses (GET /api/players/technical-kpi-breakdown?match_ids=...)
// and reading off just this player's row — no new backend code needed, since that endpoint
// already computes computeTechnicalBreakdown() per arbitrary match_ids set for every player.
// Empty selection = the default "all fetched games" view already returned by technical-trend.

function fmtDate(d) {
  if (!d) return '';
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function KpiRow({ kpi }) {
  const isPct = kpi.scoringMode === 'rate';
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 0', borderBottom: `1px solid ${C.hairline08}` }}>
      <div style={{ font: '600 12.5px ' + FONT_UI, color: 'rgba(27,37,64,.75)' }}>{kpi.kpiName}</div>
      {kpi.value == null ? (
        <div style={{ font: '400 12px ' + FONT_UI, color: C.ink40 }}>no data</div>
      ) : isPct ? (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
          <div style={{ font: '700 14px ' + FONT_UI, color: C.ink }}>{Math.round(kpi.value)}%</div>
          <div style={{ font: '500 10px ' + FONT_MONO, color: C.ink42 }}>({Math.round(kpi.count)})</div>
        </div>
      ) : (
        <div style={{ font: '700 14px ' + FONT_UI, color: C.ink }}>{Math.round(kpi.count)}</div>
      )}
    </div>
  );
}

function makeChartDot(selectedIds) {
  return function ChartDot(props) {
    const { cx, cy, payload } = props;
    if (payload.score == null) return null;
    const selected = selectedIds.includes(payload.matchId);
    return <Dot cx={cx} cy={cy} r={selected ? 6 : 4} fill={selected ? C.orange : C.plum} stroke="#fff" strokeWidth={2} />;
  };
}

export default function PlayerTechnical({ playerRecord }) {
  const navigate = useNavigate();
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewPeriod, setViewPeriod] = useState(null); // null = "last 8" default | 'season' | 'preseason'
  const [selectedIds, setSelectedIds] = useState([]);
  const [selection, setSelection] = useState(null);
  const [selectionLoading, setSelectionLoading] = useState(false);

  const togglePeriod = p => {
    setViewPeriod(cur => (cur === p ? null : p));
    setSelectedIds([]); // a prior selection may not exist in the new period's game list
  };

  useEffect(() => {
    if (!playerRecord?.id) { setLoading(false); return; }
    setLoading(true);
    getPlayerTechnicalTrend(playerRecord.id, viewPeriod ? { period: viewPeriod } : { limit: 8 })
      .then(r => setTrend(r))
      .catch(() => setTrend(null))
      .finally(() => setLoading(false));
  }, [playerRecord?.id, viewPeriod]);

  useEffect(() => {
    if (!selectedIds.length) { setSelection(null); return; }
    let cancelled = false;
    setSelectionLoading(true);
    getTechnicalKpiBreakdown(null, selectedIds)
      .then(rows => {
        if (cancelled) return;
        const mine = (rows || []).find(r => String(r.player_id) === String(playerRecord.id));
        setSelection(mine || null);
      })
      .catch(() => { if (!cancelled) setSelection(null); })
      .finally(() => { if (!cancelled) setSelectionLoading(false); });
    return () => { cancelled = true; };
  }, [selectedIds, playerRecord?.id]);

  const toggleGame = matchId => {
    setSelectedIds(ids => ids.includes(matchId) ? ids.filter(id => id !== matchId) : [...ids, matchId]);
  };

  const games = trend?.games || [];
  const chartData = games.map(g => ({
    matchId: g.matchId,
    label: g.opponentName || fmtDate(g.date),
    date: fmtDate(g.date),
    opponent: g.opponentName || 'Unknown opponent',
    score: g.score != null ? Math.round(g.score * 10) / 10 : null,
  }));

  const hasSelection = selectedIds.length > 0;
  const displayKpis = hasSelection ? (selection?.kpis || []) : (trend?.kpis || []);
  const displayOverall = hasSelection ? selection?.pillarScore : trend?.overallScore;

  return (
    <div style={{ minHeight: '100%', background: C.page, fontFamily: FONT_UI, padding: '18px 22px 30px' }}>
      <button onClick={() => navigate('/player/me')} style={{
        display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none',
        color: C.ink50, cursor: 'pointer', font: '600 13px ' + FONT_UI, padding: 0, marginBottom: 16,
      }}>
        <ChevronLeft size={16} /> Back
      </button>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <div>
          <div style={{ font: '700 22px ' + FONT_UI, letterSpacing: '-.5px', color: C.ink }}>Technical data</div>
          <div style={{ font: '500 10.5px ' + FONT_MONO, letterSpacing: '.14em', color: C.ink45, marginTop: 4 }}>
            {trend?.position ? trend.position.toUpperCase() : ''}{trend?.position ? ' · ' : ''}
            {viewPeriod === 'season' ? 'THIS SEASON' : viewPeriod === 'preseason' ? 'PRE-SEASON' : `LAST ${games.length || 8} GAMES`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          {[{ value: 'season', label: 'Season' }, { value: 'preseason', label: 'Pre-season' }].map(p => {
            const active = viewPeriod === p.value;
            return (
              <div key={p.value} onClick={() => togglePeriod(p.value)} style={{
                padding: '7px 12px', borderRadius: 11, cursor: 'pointer', font: '600 11.5px ' + FONT_UI, whiteSpace: 'nowrap',
                background: active ? C.ink : C.surface,
                color: active ? '#fff' : 'rgba(27,37,64,.7)',
                border: `1px solid ${active ? C.ink : C.hairline10}`,
                transition: 'all .2s ease',
              }}>
                {p.label}
              </div>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div style={{ marginTop: 40, textAlign: 'center', font: '400 13px ' + FONT_UI, color: C.ink45 }}>Loading…</div>
      ) : (
        <>
          {/* ── Trend chart + overall score ── */}
          <div style={{ borderRadius: 28, background: C.surface, padding: '20px 20px 12px', boxShadow: '0 2px 16px rgba(27,37,64,.05)', marginTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
              <div style={{ font: '700 15px ' + FONT_UI, letterSpacing: '-.2px', color: C.ink }}>Technical trend</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <div style={{ font: '700 22px ' + FONT_UI, color: C.plum }}>
                  {displayOverall != null ? Math.round(displayOverall) : '—'}
                </div>
                <div style={{ font: '500 10px ' + FONT_MONO, color: C.ink45, letterSpacing: '.06em' }}>OVERALL</div>
              </div>
            </div>

            {chartData.length === 0 ? (
              <div style={{ padding: '30px 0', textAlign: 'center', font: '400 13px ' + FONT_UI, color: C.ink45 }}>
                No match technical data yet.
              </div>
            ) : (
              <div style={{ height: 180, marginTop: 10 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: C.ink45, fontFamily: FONT_MONO }} axisLine={{ stroke: C.hairline10 }} tickLine={false} />
                    <YAxis domain={[0, 'dataMax + 10']} tick={{ fontSize: 10, fill: C.ink45, fontFamily: FONT_MONO }} axisLine={false} tickLine={false} width={34} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ background: C.ink, border: 'none', borderRadius: 10, padding: '8px 11px' }}
                      labelStyle={{ display: 'none' }}
                      formatter={(value, _name, props) => [`${value}`, props.payload.opponent]}
                    />
                    <Line type="monotone" dataKey="score" stroke={C.plum} strokeWidth={2.5} dot={makeChartDot(selectedIds)} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
            <div style={{ font: '400 9.5px ' + FONT_UI, color: C.ink45, marginTop: 6, marginBottom: 4 }}>
              Each point is that match's own Technical score — opponent, points scored, dribbles etc, weighted the same way the coaching staff's Technical 360 report scores it.
            </div>
          </div>

          {/* ── Game selector — pick one or more games to scope the KPI table below ── */}
          {games.length > 0 && (
            <div style={{ borderRadius: 20, background: C.surface, padding: '14px 16px', boxShadow: '0 2px 16px rgba(27,37,64,.05)', marginTop: 14 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ font: '600 11px ' + FONT_MONO, letterSpacing: '.1em', color: C.ink45 }}>SELECT GAMES</div>
                {hasSelection && (
                  <div onClick={() => setSelectedIds([])} style={{ font: '600 11.5px ' + FONT_UI, color: C.blue, cursor: 'pointer' }}>Clear</div>
                )}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                {games.map(g => {
                  const active = selectedIds.includes(g.matchId);
                  return (
                    <div key={g.matchId} onClick={() => toggleGame(g.matchId)} style={{
                      padding: '7px 12px', borderRadius: 11, cursor: 'pointer', font: '600 11.5px ' + FONT_UI, whiteSpace: 'nowrap',
                      background: active ? C.orange : C.page,
                      color: active ? '#fff' : 'rgba(27,37,64,.7)',
                      border: `1px solid ${active ? C.orange : C.hairline10}`,
                      transition: 'all .2s ease',
                    }}>
                      {g.opponentName || fmtDate(g.date)}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Personal KPI table ── */}
          <div style={{ borderRadius: 28, background: C.surface, padding: '20px 20px 8px', boxShadow: '0 2px 16px rgba(27,37,64,.05)', marginTop: 14 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
              <div style={{ font: '700 15px ' + FONT_UI, letterSpacing: '-.2px', color: C.ink }}>Your KPIs</div>
              {selectionLoading && <div style={{ font: '400 11px ' + FONT_UI, color: C.ink45 }}>Updating…</div>}
            </div>
            <div style={{ font: '400 11px ' + FONT_UI, color: C.ink45, marginTop: 3 }}>
              {hasSelection
                ? `Pooled across ${selectedIds.length} selected game${selectedIds.length > 1 ? 's' : ''}`
                : `Pooled across all ${games.length || 0} games above`}
            </div>
            <div style={{ marginTop: 8 }}>
              {displayKpis.length === 0 ? (
                <div style={{ padding: '20px 0', textAlign: 'center', font: '400 13px ' + FONT_UI, color: C.ink45 }}>No KPI data yet.</div>
              ) : (
                displayKpis.map(k => <KpiRow key={k.kpiId} kpi={k} />)
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
